import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { DOMAINS } from '@/data/domains';
import type { DomainRow } from '@/data/domains';
import type { DomainAnalyticsResponse } from '@/types/dashboard';

interface UseDomainsOptions {
  /**
   * Result cap. `'all'` requests an effectively-unbounded result (clamped
   * server-side to DASHBOARD_MAX_LIMIT). Numeric values pass through as
   * `?limit=`. A site-wide pagination primitive is scoped as a separate
   * task; this hook deliberately stays simple and passes the selector
   * value verbatim.
   */
  limit?: number | 'all';
  /**
   * When true, groups by `site` (full subdomain, e.g. `mail.google.com`)
   * instead of `root_domain` (e.g. `google.com`). Maps to the backend's
   * `?groupBySubdomain=true` query param.
   */
  groupBySubdomain?: boolean;
}

interface UseDomainsResult {
  data: readonly DomainRow[];
  isLoading: boolean;
}

export function useDomains(options: UseDomainsOptions = {}): UseDomainsResult {
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;
  const { limit, groupBySubdomain } = options;

  const query = useQuery({
    queryKey: ['domains', orgId, dateRange, limit, groupBySubdomain],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      if (limit !== undefined) {
        // Server clamps to DASHBOARD_MAX_LIMIT. `'all'` maps to that ceiling.
        qs.set('limit', limit === 'all' ? String(Number.MAX_SAFE_INTEGER) : String(limit));
      }
      if (groupBySubdomain) qs.set('groupBySubdomain', 'true');
      const qsStr = qs.toString();
      const res = await apiFetch<DomainAnalyticsResponse>(
        `/api/v1/orgs/${orgId}/dashboard/domains${qsStr ? `?${qsStr}` : ''}`,
        token,
      );
      // Task 115 — every field on the new shape is already computed by the
      // backend; the hook is a pass-through mapper.
      return res.domains.map(
        (d): DomainRow => ({
          domain: d.domain,
          appName: d.appName ?? '',
          otherAppCount: d.otherAppCount ?? 0,
          employees: d.uniqueEmployees ?? 0,
          events: d.eventCount ?? 0,
          totalHours: d.totalHours ?? 0,
          billableHours: d.billableHours ?? 0,
          nonBillableHours: d.nonBillableHours ?? 0,
          avgHoursPerUser: d.avgHoursPerUser ?? 0,
        }),
      );
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) return { data: DOMAINS, isLoading: false };
  return { data: query.data ?? [], isLoading: query.isLoading };
}
